import { useEffect, useState } from 'react'

import {
  MinimalButton,
  ScrollMode,
  SpecialZoomLevel,
  Viewer,
  ViewMode,
  Worker,
} from '@react-pdf-viewer/core'
import { NextIcon, pageNavigationPlugin, PreviousIcon } from '@react-pdf-viewer/page-navigation'
import { ThumbnailDirection, thumbnailPlugin } from '@react-pdf-viewer/thumbnail'
import '@react-pdf-viewer/core/lib/styles/index.css'
import '@react-pdf-viewer/page-navigation/lib/styles/index.css'
import '@react-pdf-viewer/thumbnail/lib/styles/index.css'
import { downloadFileSigned } from '../../investor-crm/core/_requests'
import { downloadFileSignedForContent } from '../../data-rooms/core/_requests'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useMoveToFolderData } from '../../data-rooms/core/FileToFolderAuth'
import clsx from 'clsx'

export const ContentViewUI = ({
  fileName,
  cssStyle,
  sessionToken,
  contentId,
  customClassNames
}: {
  fileName: string
  cssStyle?: boolean
  sessionToken?: string
  contentId?: any
  customClassNames?: string
}) => {
  const { formatMessage } = useIntl()

  const pageNavigationPluginInstance = pageNavigationPlugin()
  const thumbnailPluginInstance = thumbnailPlugin()
  const { Thumbnails } = thumbnailPluginInstance
  const { jumpToNextPage, jumpToPreviousPage } = pageNavigationPluginInstance
  const [signURL, setSignURL] = useState<string>('')
  const { setSignURLContext } = useMoveToFolderData()

  const getFileType = (filename: any) => {
    const extension = filename.split('.').pop()?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'svg', 'webp'].includes(extension)) {
      return 'image'
    } else if (extension === 'pdf') {
      return 'pdf'
    } else if (['ppt', 'pptx', 'docx', 'doc', 'xlsx', 'xls', 'csv'].includes(extension)) {
      return 'all'
    } else if (['mp4', 'mov', 'avi', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
      return 'video'
    } else if (
      ['mp3', 'wav', 'flac', 'aac', 'wma', 'm4a', 'ogg', 'aiff', 'midi', 'midi', 'midi'].includes(
        extension
      )
    ) {
      return 'audio'
    }
    return 'unknown'
  }

  useEffect(() => {
    const getSignedURL = async () => {
      const {
        data: { data: signedUrl, success },
      } = await downloadFileSigned(fileName)
      if (success) {
        setSignURL(signedUrl)
        setSignURLContext(signedUrl)
      }
    }

    if (fileName && !contentId && !sessionToken) {
      getSignedURL()
    }
  }, [fileName]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const getSignedURLContent = async () => {
      if (contentId && sessionToken) {
        const {
          data: { data, success, errors },
        } = await downloadFileSignedForContent(contentId, sessionToken)
        if (success) {
          setSignURL(data)
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      }
    }
    if (contentId && sessionToken) {
      getSignedURLContent()
    }
  }, [contentId, sessionToken]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (e.key === 'ArrowRight') {
        jumpToNextPage()
      } else if (e.key === 'ArrowLeft') {
        jumpToPreviousPage()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [jumpToNextPage, jumpToPreviousPage]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {(() => {
        if (!signURL || signURL === '') {
          return null
        }

        const fileType = getFileType(fileName)

        if (fileType === 'pdf') {
          return (
            <div
              style={{
                border: 'npone',
                boxShadow: '',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
              className={clsx(customClassNames, 'my-5 mx-6')}
            >
              <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js'>
                <div
                  style={{
                    borderBottom: '1px solid rgba(0, 0, 0, .3)',
                    height: '100%',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '1rem',
                      transform: 'translate(0, -100%) rotate(-90deg)',
                      zIndex: '1',
                    }}
                  >
                    <MinimalButton onClick={jumpToPreviousPage}>
                      <PreviousIcon />
                    </MinimalButton>
                  </div>
                  {fileName && (
                    <Viewer
                      defaultScale={SpecialZoomLevel.PageFit}
                      scrollMode={ScrollMode.Page}
                      viewMode={ViewMode.SinglePage}
                      fileUrl={signURL}
                      plugins={[pageNavigationPluginInstance, thumbnailPluginInstance]}
                    />
                  )}

                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '1rem',
                      transform: 'translate(0, -100%) rotate(-90deg)',
                      zIndex: '1',
                    }}
                  >
                    <MinimalButton onClick={jumpToNextPage}>
                      <NextIcon />
                    </MinimalButton>
                  </div>
                </div>
                <div
                  style={{
                    height: '12rem',
                    overflow: 'auto',
                  }}
                >
                  <Thumbnails thumbnailDirection={ThumbnailDirection.Horizontal} />
                </div>
              </Worker>
            </div>
          )
        } else if (fileType === 'image') {
          return (
            <div
              style={{
                height: cssStyle ? '' : '92vh',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <div
                className={clsx('image-container ', customClassNames)}
                style={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  margin: 'auto',
                }}
              >
                <img
                  src={signURL}
                  alt={fileName}
                  style={{
                    width: '100%',
                    height: '93vh',
                    objectFit: 'contain',
                  }}
                />
              </div>
            </div>
          )
        } else if (fileType === 'all') {
          return (
            <div className={clsx('iframe-container my-5 mx-6 ', customClassNames)}>
              <iframe
                width='100%'
                height='560px'
                title='Embedded Document'
                src={`https://docs.google.com/gview?url=${encodeURIComponent(signURL).replace(
                  /'/g,
                  '%27'
                )}&embedded=true`}
              ></iframe>
            </div>
          )
        } else if (fileType === 'video') {
          return (
            <div
              className={clsx(
                'iframe-container my-5 mx-6 d-flex justify-content-center h-100 align-items-center',
                customClassNames
              )}
            >
              <video src={signURL} width='95%' height='400px' controls autoPlay>
                <source src={signURL} type={`video/${fileName.split('.').pop()}`} />
              </video>
            </div>
          )
        } else if (fileType === 'audio') {
          return (
            <div
              className={clsx(
                'iframe-container my-5 mx-6 w-100 d-flex justify-content-center h-100 align-items-center',
                customClassNames
              )}
            >
              <audio controls autoPlay>
                <source src={signURL} type={`audio/${fileName.split('.').pop()}`} />
              </audio>
            </div>
          )
        } else {
          return null
        }
      })()}
    </>
  )
}
